import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import {
  activityParamKey,
  difficultyParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  lengthParamKey,
  minRatingParamKey,
  routeTypeParamKey,
  suitabilityParamKey,
  trailTrafficParamKey,
  trailTrafficParamValues
} from './exploreSearchParams';

type CreateExploreUrlOptions = {
  languageRegionCode: LanguageRegionCode;
  ref?: string;
};

/**
 * Create an explore page URL from explore-service filters
 * Uses the same parameter keys as buildSearchParams for consistency
 * @param filters - The explore-service filters to convert to URL parameters
 * @param options - Configuration options for URL generation
 * @returns The full explore page URL with query parameters
 */
export const createExploreUrlFromFilters = (filters: ExploreServiceFilters, options: CreateExploreUrlOptions): string => {
  const { languageRegionCode, ref } = options;
  const params = new URLSearchParams();

  // Activity - use first activity only
  if (filters.activity?.length) {
    params.append(activityParamKey, filters.activity[0]);
  }

  // Difficulty
  if (filters.difficulty?.length) {
    filters.difficulty.forEach(d => params.append(difficultyParamKey, d));
  }

  // Feature
  if (filters.feature?.length) {
    filters.feature.forEach(f => params.append(featureParamKey, f));
  }

  // Suitability
  if (filters.suitability?.length) {
    filters.suitability.forEach(s => params.append(suitabilityParamKey, s));
  }

  // Route Type
  if (filters.routeType?.length) {
    filters.routeType.forEach(r => params.append(routeTypeParamKey, r));
  }

  // Min Rating
  if (filters.minRating) {
    params.append(minRatingParamKey, String(filters.minRating));
  }

  // Length
  if (filters.length) {
    params.append(lengthParamKey, String(filters.length.min ?? 0));
    params.append(lengthParamKey, String(filters.length.max ?? -1));
  }

  // Duration
  if (filters.duration) {
    params.append(durationParamKey, String(filters.duration.min ?? 0));
    params.append(durationParamKey, String(filters.duration.max ?? -1));
  }

  // Elevation Gain
  if (filters.elevationGain) {
    params.append(elevationGainParamKey, String(filters.elevationGain.min ?? 0));
    params.append(elevationGainParamKey, String(filters.elevationGain.max ?? -1));
  }

  // Trail Traffic
  if (filters.trailTraffic?.length) {
    filters.trailTraffic.forEach(t => params.append(trailTrafficParamKey, trailTrafficParamValues[t]));
  }

  // Highest Point
  if (filters.highestPoint) {
    params.append(highestPointParamKey, String(filters.highestPoint.min ?? 0));
    params.append(highestPointParamKey, String(filters.highestPoint.max ?? -1));
  }

  // Build base path
  const basePath = languageRegionCode === 'en-US' ? '/explore' : `/${languageRegionCode}/explore`;

  // Build query string
  const queryString = params.toString();
  const queryParts: string[] = [];

  if (queryString) {
    queryParts.push(queryString);
  }

  if (ref) {
    queryParts.push(`ref=${ref}`);
  }

  const finalQueryString = queryParts.join('&');

  return finalQueryString ? `${basePath}?${finalQueryString}` : basePath;
};
